import {
  action,
  computed,
  Lambda,
  makeObservable,
  onBecomeObserved,
} from "mobx"

import GlobalProjectsCollection from "@src/app/ProjectsCollection"
import { statusOptions } from "@src/pages/paymentLinks/constants"
import { catchErrors, invoicesApi } from "@src/shared/api"
import { InvoiceResponse } from "@src/shared/api/generated"
import { CollectionWithPagination } from "@src/shared/classes/Collection/CollectionWithPagination"
import { loading } from "@src/shared/classes/utils/loading"
import { CollectionFilters } from "@src/shared/componemts/TableFilter/classes/CollectionFilters"
import { filtersPresets } from "@src/shared/componemts/TableFilter/classes/constants"

export type InvoiceCollectionLoadRequest = {
  id?: string
  order?: "asc" | "desc"
  page?: number
  projectId?: string
  status?: "open" | "paid" | "unresolved" | "viewed" | "void"
}

export type InvoiceCollectionFilter = {
  id?: string
  order?: "asc" | "desc"
  projectId?: string
  status?: "open" | "paid" | "unresolved" | "viewed" | "void"
}

export class InvoiceCollection extends loading(
  ["fetch"],
  CollectionWithPagination<InvoiceResponse>,
) {
  filter = new CollectionFilters(this.fConf, this.load)

  constructor(private _projects = GlobalProjectsCollection()) {
    super()

    makeObservable(this, {
      fConf: computed,
      fetch: action.bound,
    })

    const kill: Lambda = onBecomeObserved(this, "isLoading", () => {
      kill()

      return this.load()
    })
  }

  async fetch(req: InvoiceCollectionLoadRequest, signal: AbortSignal) {
    try {
      const { data: { data } = {} } = await invoicesApi.getInvoices(
        req.order,
        this.calcOffset(req.page),
        this.pageSize,
        req.projectId,
        req.status,
        req.id,
        { signal },
      )

      if (data) {
        return {
          data: data.data!,
          pagination: data.pagination!,
        }
      }
    } catch (e: any) {
      catchErrors(e)
    }

    return null
  }

  get fConf() {
    return [
      CollectionFilters.createField("id").search({
        col: 6,
        label: "Search for ID",
        placeholder: "ID",
      }),
      CollectionFilters.createField("projectId").select(
        this._projects.selectOptions,
        {
          col: 6,
          isLoading: this._projects.isLoading,
          label: "Select project",
          placeholder: "Select",
        },
      ),
      CollectionFilters.createField("status").select(statusOptions, {
        col: 6,
        placeholder: "Select",
      }),
      {
        ...filtersPresets.order,
        col: 6,
        label: "Select an order",
        placeholder: "Select",
      },
    ]
  }
}
